import { assertUnreachable } from "common/utils/assertUnreachable";
import { faceCentroid, type Extrusion, type Face } from "geom/brep";
import type { Geom } from "geom/geom";
import { geomToFaces } from "geom/geom";
import { vec3Add } from "./vec3";

export default class GeomTagGroup<T> {
  public geom: Geom[] = [];
  public tags: T[] = [];

  add(geo: Geom, tag: T): GeomTagGroup<T> {
    if (!geo) {
      return this;
    }

    this.geom.push(geo);
    this.tags.push(tag);
    return this;
  }

  addMany(geo: Geom[], tag: T): GeomTagGroup<T> {
    if (!geo) {
      return this;
    }

    for (const g of geo) {
      this.geom.push(g);
      this.tags.push(tag);
    }
    return this;
  }

  toFaces(filter?: (tag: T) => boolean): Face[] {
    const faces: Face[] = [];

    for (let i = 0; i < this.geom.length; i++) {
      if (!filter || filter(this.tags[i])) {
        faces.push(...geomToFaces(this.geom[i]));
      }
    }
    return faces;
  }

  collectExtrusions(extrusions: Extrusion[]) {
    for (const geom of this.geom) {
      if (geom.type === "EXTRUSION") {
        extrusions.push(geom);
      }
    }
  }

  collectExtrusionsFaces(extrusions: Extrusion[], faces: Face[]) {
    for (const geom of this.geom) {
      switch (geom.type) {
        case "EXTRUSION":
          extrusions.push(geom);
          break;
        case "FACE":
          faces.push(geom);
          break;
        case "BREP":
          faces.push(...geom.faces);
          break;
        case "FACE_GROUP":
          faces.push(...geom.faces);
          break;
      }
    }
  }

  samplePoint() {
    if (this.geom.length === 0) {
      return undefined;
    }
    const geom = this.geom[0];
    switch (geom.type) {
      case "EXTRUSION":
        const centroid = faceCentroid(geom.profile);
        return centroid ? vec3Add(...centroid, geom.dirX / 2, geom.dirY / 2, geom.dirZ / 2) : undefined;
      case "FACE":
        return faceCentroid(geom);
      case "BREP":
        return faceCentroid(geom.faces[0]);
      case "FACE_GROUP":
        return faceCentroid(geom.faces[0]);
      default:
        assertUnreachable(geom);
    }
  }
}
